import { useCallback, useEffect, useState } from "react";
import { Bell } from "lucide-react";
import ApplicationSwitcher from "@/components/shell/application-switcher";
import OrgSwitcher from "@/components/shell/org-switcher";
import ProfileDropdown from "@/components/shell/profile-dropdown";
import { Tooltip } from "@/components/ui/tooltip";
import { useAuthStore } from "@/stores/auth-store";
import { fetchNotifications } from "@/lib/api";
import { cn } from "@/lib/utils";

function IconButton({
  label,
  children,
  dot = false,
}: {
  label: string;
  children: React.ReactNode;
  dot?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      className="relative grid min-h-9 min-w-9 place-items-center rounded-md border border-transparent text-muted transition-colors hover:bg-bg hover:border-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
    >
      {children}
      {dot && (
        <span aria-hidden className="absolute right-1.5 top-1.5 size-2 rounded-full border-2 border-surface bg-danger" />
      )}
    </button>
  );
}

function EnvironmentBadge({ mode }: { mode: string }) {
  if (!mode) return null;
  const isProduction = mode === "production" || mode === "live";
  return (
    <span
      data-testid="env-badge"
      className={cn(
        "hidden items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold sm:inline-flex",
        isProduction
          ? "bg-danger/10 text-danger"
          : "bg-info/10 text-info",
      )}
    >
      <span
        aria-hidden
        className={cn("size-1.5 rounded-full", isProduction ? "bg-danger" : "bg-info")}
      />
      {mode.charAt(0).toUpperCase() + mode.slice(1)}
    </span>
  );
}

export default function Topbar() {
  const [unread, setUnread] = useState(0);
  const experience = useAuthStore((s) => s.experience);

  const loadNotifications = useCallback(async () => {
    const result = await fetchNotifications();
    const count = result.notifications.filter(
      (n) => n && typeof n === "object" && !(n as Record<string, unknown>).read,
    ).length;
    setUnread(count);
  }, []);

  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  return (
    <header
      data-testid="topbar"
      className="flex h-[68px] shrink-0 items-center justify-between gap-3.5 border-b border-line bg-surface px-6"
    >
      <div className="relative hidden w-full max-w-[266px] shrink items-center md:flex">
        <span aria-hidden className="pointer-events-none absolute left-2.5 z-1 text-muted">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <circle cx="6.5" cy="6.5" r="4.5" />
            <line x1="10.5" y1="10.5" x2="14" y2="14" />
          </svg>
        </span>
        <input
          type="text"
          placeholder="Search…"
          aria-label="Search"
          className="h-9 w-full rounded border border-line bg-surface pl-9 pr-12 text-sm text-ink outline-none transition-colors placeholder:text-muted focus:border-primary"
        />
        <kbd aria-hidden className="pointer-events-none absolute right-2.5 text-[11px] text-text">⌘ F</kbd>
      </div>

      <div className="ml-auto flex items-center gap-1">
        <EnvironmentBadge mode={experience.environment.mode} />
        <OrgSwitcher />
        <Tooltip content="Switch application">
          <ApplicationSwitcher />
        </Tooltip>
        <Tooltip content={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"}>
          <IconButton label={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"} dot={unread > 0}>
            <Bell aria-hidden className="size-4" />
          </IconButton>
        </Tooltip>

        <div className="ml-1 flex items-center gap-2.5 rounded-md px-2 py-1">
          <Tooltip content="Account">
            <ProfileDropdown />
          </Tooltip>
        </div>
      </div>
    </header>
  );
}
